import { useSearchResults, useSearchFilters, useSearchContext } from '../contexts/SearchContext';
import { Hit } from './Hit/index.tsx';
import type { DisplayJob } from '../types';
import BannerModule from './BannerModule';
import { trackEvent } from '../utils/analytics/analytics';
import { getLocaleFromPathname } from '../utils/routing/locale-from-pathname';
import React from 'react';
import SavedStateContext from '../contexts/SavedStateContext';
// Step 4: Lazy load MapView so mapbox-gl and map UI load only when user switches to map view
const MapViewLazy = React.lazy(() =>
  import('./MapView').then((m) => ({ default: m.MapView }))
);
import { PerformanceProfiler, useRenderLogger } from '../utils/shared/performance-profiler';
import { FormattedMessage } from 'react-intl';
import { SrpResultsGridSkeleton } from './search/SrpResultsGridSkeleton';
import { spliceResultsInterstitial } from '../utils/srp/srp-results-interstitial';
import { buildRoleClusterKey, groupHitsByCompany } from '../utils/srp/group-hits-by-company';
import { resolveSearchHitCompanyId } from '../utils/search/resolve-search-hit-company-display';
import { HitCompanyGroupCard } from './Hit/HitCompanyGroupCard';
import { Msg } from '../i18n-review/client/Msg';

interface CustomHitsProps {
  withBanners?: boolean;
  viewMode?: 'list' | 'map';
  /** Kept for callers wiring up the shared view-mode toggle in SearchSidebar; not read here. */
  setViewMode?: (mode: 'list' | 'map') => void;
  /** Rendered in-feed after the 3rd (mobile) / 6th (desktop) card, e.g. the top-employers strip. Null/omitted renders nothing. */
  interstitial?: React.ReactNode;
  /** Rendered 2 grid rows after `interstitial` in each layout, e.g. the top-roles strip. */
  secondInterstitial?: React.ReactNode;
  /** Map mode only: floated over the top of the map pane (see MapView.tsx's `mapOverlay` prop),
   * e.g. the SRP's "get email alerts" CTA, instead of sitting in normal document flow above it. */
  mapOverlay?: React.ReactNode;
}

const staticBanners = [
  <BannerModule key="banner-save" text="Save jobs you are interested in to compare salaries and benefits side-by-side." />,
  <BannerModule
    key="banner-login"
    text="Log in and add your current job details to see how your salary compares to new opportunities in Finland."
    onClickEvent={async () => {
      // Send a custom event
      trackEvent({
        name: 'auth_interaction',
        params: {
          action: 'sign_in_click',
          surface: 'search_results',
          locale: typeof window !== 'undefined' ? (getLocaleFromPathname(window.location.pathname) ?? undefined) : undefined,
        },
      });
      // TODO:: Re-enable auth0
      // const { loginWithRedirect } = useAuth0();
      // await loginWithRedirect({
      //   appState: {
      //     returnTo: '/en/user-profile-overview',
      //   },
      // });
    }}
  />,
];

// Custom comparison function for React.memo to prevent unnecessary re-renders
// CustomHits re-renders are controlled by the context hooks inside, so we only
// need to check if the props themselves changed
function areCustomHitsPropsEqual(prevProps: CustomHitsProps, nextProps: CustomHitsProps): boolean {
  const withBannersSame = prevProps.withBanners === nextProps.withBanners;
  const viewModeSame = prevProps.viewMode === nextProps.viewMode;
  const interstitialSame = prevProps.interstitial === nextProps.interstitial;
  const secondInterstitialSame = prevProps.secondInterstitial === nextProps.secondInterstitial;
  const mapOverlaySame = prevProps.mapOverlay === nextProps.mapOverlay;
  return withBannersSame && viewModeSame && interstitialSame && secondInterstitialSame && mapOverlaySame;
}

export const CustomHits = React.memo(function CustomHits({ withBanners = false, viewMode: propViewMode, interstitial = null, secondInterstitial = null, mapOverlay = null }: CustomHitsProps) {
  const renderCountRef = React.useRef(0);
  const isFirstRender = renderCountRef.current === 0;
  renderCountRef.current += 1;
  const prevContextValueRef = React.useRef<any>(null);
  const prevResultsRef = React.useRef<any>(null);
  const prevResultsHitsRef = React.useRef<any[] | null>(null);
  
  const { results } = useSearchResults();
  const { filters } = useSearchFilters();
  // Typesense facet counts are exact over ALL matching docs (unlike the capped hit sample), so
  // the map view can use them to show accurate nationwide job density. Keyed by geo-grid-cell
  // (utils/typesense/geo-cell.ts), not city name, a cell key decodes straight back to real
  // coordinates without a hand-curated city lookup table, at a resolution far finer than the
  // ~30 named cities a `locationList` breakdown would give.
  const geoCellCounts = results?.facets?.geoCell as Record<string, number> | undefined;
  // Exact per-region job counts (Finland's real 18 admin1/maakunta regions, GEO_CELL_TIERS in
  // geo-cell.ts) — the zoomed-all-the-way-out tier of the map's density hierarchy, keyed by
  // GeoNames placeId (same id space `regionTags` uses for admin1-type values).
  const regionAdmin1Counts = results?.facets?.regionAdmin1 as Record<string, number> | undefined;
  // Exact per-district (~2km cell) job counts — the map's finest aggregate tier, one step above
  // real per-job pins. Only present once the live viewport is small enough that SearchContext
  // actually requested this facet (see its map-mode query); undefined otherwise, same as any
  // other facet Typesense wasn't asked for.
  const geoCellDistrictCounts = results?.facets?.geoCellDistrict as Record<string, number> | undefined;
  // Recent-jobs-only facet counts, for the map's "trending" pulse (recent vs. total per cell).
  const recentGeoCellCounts = (results as any)?.recentFacets?.geoCell as Record<string, number> | undefined;
  // TEC-482: regionTags is already requested on every query (utils/facets/facets-shared.ts's
  // FACET_ATTRIBUTES) regardless of the new-location-system cookie, so this is free, the map
  // (new-system only) filters it down to country-type values itself to draw zoomed-out country
  // bubbles for jobs it has no real geoloc pin for.
  const regionTagsCounts = results?.facets?.regionTags as Record<string, number> | undefined;
  const contextValue = useSearchContext();
  const { isSearchLoading, perPage } = contextValue;
  
  // Track what's changing from hooks (only on UPDATE renders)
  const prevResultsFromHookRef = React.useRef(results);
  const prevFiltersFromHookRef = React.useRef(filters);
  
  if (!isFirstRender && typeof window !== 'undefined') {
    prevResultsFromHookRef.current = results;
    prevFiltersFromHookRef.current = filters;
    
    // Check if contextValue reference changed
    const contextValueRefChanged = prevContextValueRef.current !== contextValue;
    
    // Track contextValue reference changes (only on updates)
    if (contextValueRefChanged) {
      const prevCtx = prevContextValueRef.current;
      const changes: string[] = [];
      
      if (prevCtx) {
        if (prevCtx.query !== contextValue.query) changes.push('query');
        if (prevCtx.page !== contextValue.page) changes.push('page');
        if (prevCtx.sort !== contextValue.sort) changes.push('sort');
        if (prevCtx.viewMode !== contextValue.viewMode) changes.push('viewMode');
        if (prevCtx.results !== contextValue.results) {
          changes.push('results');
          // Check if hit object refs changed (same IDs but new objects)
          const prevHits = prevCtx.results?.hits || [];
          const currHits = contextValue.results?.hits || [];
          if (prevHits.length === currHits.length) {
            const hitRefsChanged = prevHits.some((prevHit: any, idx: number) => {
              const currHit = currHits[idx];
              return prevHit !== currHit && (prevHit?.objectID || prevHit?.id) === (currHit?.objectID || currHit?.id);
            });
            if (hitRefsChanged) {
              changes.push('results.hits[*] (object refs changed)');
            }
          }
        }
        if (prevCtx.filters !== contextValue.filters) changes.push('filters');
      } else {
        changes.push('(prev context was null)');
      }
    }
    prevContextValueRef.current = contextValue;
    prevResultsHitsRef.current = results?.hits || null;
    prevResultsRef.current = results;
  } else {
    // Initialize refs on first render
    prevContextValueRef.current = contextValue;
    prevResultsRef.current = results;
    prevResultsHitsRef.current = results?.hits || null;
  }
  const { viewMode: contextViewMode, setMapBounds } = contextValue;
  
  // Determine viewMode for variant computation
  // IMPORTANT: SearchProvider initializes AFTER LayoutContent, so contextViewMode may be undefined on first render
  // We MUST use propViewMode (from SSR-initialized useViewMode) for initial render, not contextViewMode
  // propViewMode comes from useViewMode which is initialized synchronously from SSR initialLayout (from cookies)
  // useViewMode always returns a valid ViewMode (defaults to 'list' if initialLayout is undefined)
  // 
  // For initial render: Use propViewMode only (SSR data available at mount time)
  // For subsequent renders: Can use contextViewMode as fallback (after SearchProvider has initialized)
  const viewMode: 'list' | 'map' = propViewMode ?? 'list';
  
  // Compute variant from viewMode synchronously - ensures it's available from first render
  // This prevents variant from being undefined on first render, then changing to compact
  // Always return a valid variant, never undefined - viewMode is 'list' | 'map'
  const variant: 'card' | 'compact' = viewMode === 'list' ? 'compact' : 'card';
  
  // Runtime assertion - variant should ALWAYS be defined at this point
  // If it's not, something is seriously wrong with the initialization
  if (variant !== 'compact' && variant !== 'card') {
    throw new Error(`[CustomHits] CRITICAL: Invalid variant computed: ${variant}. viewMode: ${viewMode}, propViewMode: ${propViewMode}`);
  }
  
  
  const savedState = React.useContext(SavedStateContext);
  
  // Stable reference map for hit objects - keyed by objectID to preserve references
  const hitsMapRef = React.useRef<Map<string, DisplayJob>>(new Map());
  
  // Memoize allHits and stabilize individual hit object references
  const allHits = React.useMemo(() => {
    const rawHits = results?.hits;
    const newHits = Array.isArray(rawHits) ? rawHits : [];
    const hitsMap = hitsMapRef.current;
    const stableHits: DisplayJob[] = [];
    let reusedCount = 0;
    let newCount = 0;
    
    // For each hit, use existing reference if available, otherwise add to map
    // This is critical - if results?.hits gets a new array with new object references,
    // we want to reuse the old object references to prevent Hit components from re-rendering
    for (const hit of newHits) {
      const hitId = hit?.objectID || hit?.id;
      if (hitId) {
        const hitIdStr = String(hitId);
        const existingHit = hitsMap.get(hitIdStr);
        // If we have an existing hit with same ID, ALWAYS use that reference
        // This prevents re-renders when hit objects get new references but same content
        if (existingHit) {
          // Verify the ID still matches (safety check)
          const existingHitId = existingHit.objectID || existingHit.id;
          if (String(existingHitId) === hitIdStr) {
            // Always use existing hit reference (even if same as new hit)
            stableHits.push(existingHit);
            if (existingHit !== hit) {
              reusedCount++;
            }
            // If same reference, just continue (already pushed)
          } else {
            // ID changed - update the map
            hitsMap.set(hitIdStr, hit);
            stableHits.push(hit);
            newCount++;
          }
        } else {
          // New hit - add to map
          hitsMap.set(hitIdStr, hit);
          stableHits.push(hit);
          newCount++;
        }
      } else {
        stableHits.push(hit);
        newCount++;
      }
    }
    
    // Clean up map - remove hits that are no longer in results
    const currentIds = new Set(newHits.map(h => String(h?.objectID || h?.id || '')));
    let removedCount = 0;
    for (const [id] of hitsMap) {
      if (!currentIds.has(id)) {
        hitsMap.delete(id);
        removedCount++;
      }
    }
    
    return stableHits;
  }, [results?.hits]);
  
  // Memoize filterObj to prevent creating new object reference on every render
  // Use a stable empty object to prevent reference changes
  const emptyFilterObj = React.useMemo(() => {
    return {};
  }, []);
  const filterObj = React.useMemo(() => {
    return (filters && typeof filters === 'object') ? (filters as Record<string, string[]>) : emptyFilterObj;
  }, [filters, emptyFilterObj]);
  
  // Memoize exclude flags to prevent recalculation
  const excludeSaved = React.useMemo(() => {
    return filterObj.excludeSaved?.includes('true') || false;
  }, [filterObj.excludeSaved]);

  const excludeViewed = React.useMemo(() => {
    return filterObj.excludeViewed?.includes('true') || false;
  }, [filterObj.excludeViewed]);

  const excludeApplied = React.useMemo(() => {
    return filterObj.excludeApplied?.includes('true') || false;
  }, [filterObj.excludeApplied]);
  
  // Stable reference for filtered hits - only recreate if content actually changed
  const hitsRef = React.useRef<DisplayJob[]>([]);
  const hitsVersionRef = React.useRef<number>(0);
  const prevSlicedHitsRef = React.useRef<DisplayJob[]>([]);
  
  // Filter hits based on exclude filters only
  // Hidden jobs are already filtered out server-side in +data.ts
  const hits = React.useMemo(() => {
    const filtered = allHits.filter((hit: DisplayJob) => {
        const jobId = hit.objectID || hit.id;
        if (!jobId) return true;
        
        // Exclude if in saved jobs and excludeSaved is true
        if (excludeSaved && savedState?.saved?.has(String(jobId))) {
          return false;
        }
        
        // Exclude if in viewed jobs and excludeViewed is true
        if (excludeViewed && savedState?.viewed?.has(String(jobId))) {
          return false;
        }
        
        // Exclude if in applied jobs and excludeApplied is true
        if (excludeApplied && savedState?.applied?.has(String(jobId))) {
          return false;
        }
        
        return true;
      });
    
    // Check if the filtered results are actually different from previous
    const prevHits = hitsRef.current;
    const hitsChanged = 
      prevHits.length !== filtered.length ||
      prevHits.some((prevHit, idx) => {
        const currHit = filtered[idx];
        return (prevHit.objectID || prevHit.id) !== (currHit.objectID || currHit.id) ||
               prevHit !== currHit; // Check reference too
      });
    
    if (hitsChanged) {
      hitsRef.current = filtered;
      hitsVersionRef.current += 1;
    }
    
    // Return stable reference if content hasn't changed
    const finalHits = hitsChanged ? filtered : prevHits;
    
    // Check if the hits array is the same as previous (same objects, same order)
    const prevHitsArray = prevSlicedHitsRef.current;
    const hitsArrayChanged = 
      prevHitsArray.length !== finalHits.length ||
      prevHitsArray.some((prevHit, idx) => {
        const currHit = finalHits[idx];
        return prevHit !== currHit || (prevHit.objectID || prevHit.id) !== (currHit.objectID || currHit.id);
      });
    
    if (hitsArrayChanged) {
      prevSlicedHitsRef.current = finalHits;
      // Only log on UPDATE renders
      if (typeof window !== 'undefined' && renderCountRef.current > 1) {
        const prevIds = prevHitsArray.map(h => h.objectID || h.id);
        const currIds = finalHits.map(h => h.objectID || h.id);
        const idsChanged = prevIds.join(',') !== currIds.join(',');
        const refsChanged = prevHitsArray.some((prev, idx) => prev !== finalHits[idx]);
      }
    } else {
      // Return previous array reference to maintain stability
      return prevHitsArray;
    }
    
    return finalHits;
  }, [allHits, excludeSaved, excludeViewed, excludeApplied, savedState, viewMode]);

  // Same-page-only grouping (utils/srp/group-hits-by-company.ts): a company with 2+ open roles
  // on this page renders as HitCompanyGroupCards instead of several near-identical cards, split
  // into multiple cards of up to MAX_ROLES_PER_GROUP_CARD roles, clustered by field/title. The
  // cluster key must mirror SearchResults.tsx's static grouping (same field + normalized title
  // inputs) or hydration reshuffles the grid.
  const groupedHits = React.useMemo(
    () =>
      groupHitsByCompany(
        hits,
        (hit) => resolveSearchHitCompanyId(hit as unknown as Record<string, unknown>),
        (hit) => {
          const job = hit as unknown as DisplayJob;
          return buildRoleClusterKey(job.field, job.normalisedTitle ?? job.title);
        },
      ),
    [hits],
  );

  // Detailed performance logging - track ALL dependencies that could cause re-renders
  // Moved after hits definition to avoid temporal dead zone error
  useRenderLogger('CustomHits', {
    // Props
    withBanners,
    propViewMode,
    // Context values
    viewMode,
    contextViewMode,
    // Results
    resultsReference: results,
    resultsNbHits: results?.nbHits,
    resultsPage: results?.page,
    resultsNbPages: results?.nbPages,
    hitsCount: results?.hits?.length,
    allHitsLength: allHits.length,
    hitsLength: hits.length,
    // Filters
    filtersReference: filters,
    filtersType: typeof filters,
    filterObjKeys: Object.keys(filterObj),
    filterObjReference: filterObj,
    excludeSaved,
    excludeViewed,
    excludeApplied,
    // Saved state
    savedStateReference: savedState,
    savedStateSavedSize: savedState?.saved?.size,
    savedStateViewedSize: savedState?.viewed?.size,
    savedStateAppliedSize: savedState?.applied?.size,
    // Context callbacks (check if they're changing)
    setMapBoundsReference: setMapBounds,
    contextValueReference: contextValue,
  });
  
  // Track previous values to detect what's changing
  const prevValuesRef = React.useRef<{
    results?: any;
    resultsHits?: any[];
    filters?: any;
    savedState?: any;
    contextValue?: any;
    hits?: DisplayJob[];
    hitsLength?: number;
  }>({});
  
  // Track previous values to detect changes
  React.useEffect(() => {
    // Skip on first render
    if (renderCountRef.current === 1) {
      prevValuesRef.current = {
        results,
        resultsHits: results?.hits,
        filters,
        savedState,
        contextValue,
        hits,
        hitsLength: hits.length,
      };
      return;
    }
    
    const prev = prevValuesRef.current;
    const changes: string[] = [];
    
    if (prev.results !== results) {
      changes.push(`results ref changed (prev: ${prev.results?.nbHits}, curr: ${results?.nbHits})`);
    }
    if (prev.resultsHits !== results?.hits) {
      changes.push(`results.hits ref changed (prev length: ${prev.resultsHits?.length}, curr: ${results?.hits?.length})`);
    }
    if (prev.filters !== filters) {
      changes.push('filters ref changed');
    }
    if (prev.savedState !== savedState) {
      changes.push('savedState ref changed');
    }
    if (prev.contextValue !== contextValue) {
      changes.push('contextValue ref changed');
    }
    if (prev.hits !== hits) {
      changes.push(`hits array ref changed (prev length: ${prev.hitsLength}, curr: ${hits.length})`);
    }
    if (prev.hitsLength !== hits.length) {
      changes.push(`hits length changed (${prev.hitsLength} -> ${hits.length})`);
    }
    
    // Update previous values
    prevValuesRef.current = {
      results,
      resultsHits: results?.hits,
      filters,
      savedState,
      contextValue,
      hits,
      hitsLength: hits.length,
    };
  });
  
  // Memoize sendEvent to prevent recreating on every render (causes Hit components to re-render)
  // Use a ref to ensure the function reference is truly stable across all renders
  const sendEventRef = React.useRef<() => void>(() => {});
  const sendEvent = React.useCallback(() => {
    return sendEventRef.current();
  }, []);
  
  // Track sendEvent reference for stability
  const prevSendEventRef = React.useRef(sendEvent);
  prevSendEventRef.current = sendEvent;

  // Memoize map bounds change handler to prevent recreating on every render
  const handleMapBoundsChange = React.useCallback((bounds: any) => {
    // TODO: Trigger geofence search with bounds
    setMapBounds(bounds);
  }, [setMapBounds]);

  // Note: No-results handling is done by the NoResults component in the parent
  // This component should not show its own no-results message
  // However, we still need to render something to maintain layout
  // The parent will conditionally render NoResults instead of CustomHits when appropriate
  // Exception: Always render MapView in map mode, even with no hits
  if (hits.length === 0 && viewMode !== 'map') {
    if (isSearchLoading) {
      return (
        <PerformanceProfiler id="CustomHits">
          <section className="srp-results" aria-labelledby="results-title">
            <h2 id="results-title" className="sr-only">
              <Msg id="search.results.title" defaultMessage="Job Search Results" description="Screen reader heading for the job search results section" />
            </h2>
            <SrpResultsGridSkeleton count={perPage} />
          </section>
        </PerformanceProfiler>
      );
    }
    // Return empty div to maintain layout, parent will handle showing NoResults
    return <div className="w-full" />;
  }

  const renderContent = () => {
    if (!withBanners) {
      if (viewMode === 'map') {
        return (
          <div className="relative w-full sm:px-0 md:px-2 lg:px-2 xl:px-3 2xl:px-4">
            <React.Suspense fallback={<div className="min-h-[400px] w-full bg-gray-100 dark:bg-gray-800 animate-pulse rounded-lg" aria-hidden />}>
              <MapViewLazy
                jobs={hits as DisplayJob[]}
                onBoundsChange={handleMapBoundsChange}
                geoCellCounts={geoCellCounts}
                regionAdmin1Counts={regionAdmin1Counts}
                geoCellDistrictCounts={geoCellDistrictCounts}
                recentGeoCellCounts={recentGeoCellCounts}
                regionTagsCounts={regionTagsCounts}
                totalJobsInView={results?.nbHits}
                mapOverlay={mapOverlay}
              />
            </React.Suspense>
          </div>
        );
      }
      
      return (
        <section className="srp-results" aria-labelledby="results-title">
          <h2 id="results-title" className="sr-only">
            <Msg id="search.results.title" defaultMessage="Job Search Results" description="Screen reader heading for the job search results section" />
          </h2>
          <ul className="srp-results-grid">
            {spliceResultsInterstitial(
              groupedHits,
              (entry, index) =>
                entry.type === 'group' ? (
                  <li
                    key={`group-${entry.companyId}-${entry.partIndex}`}
                    className={`srp-results-grid-item ${entry.items.length >= 3 ? 'srp-results-grid-item--span-2' : ''}`}
                  >
                    <HitCompanyGroupCard items={entry.items} sendEvent={sendEvent} index={index} />
                  </li>
                ) : (
                  <li
                    key={(entry.item as unknown as DisplayJob).objectID}
                    className="srp-results-grid-item"
                  >
                    <Hit hit={entry.item as unknown as DisplayJob} sendEvent={sendEvent} variant={variant} index={index} />
                  </li>
                ),
              interstitial,
              undefined,
              secondInterstitial
            )}
          </ul>
        </section>
      );
    }

    // Responsive rendering with banners
    if (viewMode === 'map') {
      return (
        <div className="relative w-full max-w-full overflow-x-hidden @sm:px-0 @md:px-2 @lg:px-2 @xl:px-3 @2xl:px-4">
          <React.Suspense fallback={<div className="min-h-[400px] w-full bg-gray-100 dark:bg-gray-800 animate-pulse rounded-lg" aria-hidden />}>
            <MapViewLazy
              jobs={hits as DisplayJob[]}
              onBoundsChange={handleMapBoundsChange}
              geoCellCounts={geoCellCounts}
              regionAdmin1Counts={regionAdmin1Counts}
                geoCellDistrictCounts={geoCellDistrictCounts}
              recentGeoCellCounts={recentGeoCellCounts}
              regionTagsCounts={regionTagsCounts}
              totalJobsInView={results?.nbHits}
              mapOverlay={mapOverlay}
            />
          </React.Suspense>
        </div>
      );
    }
    
    return (
      <section className="w-full max-w-full overflow-x-hidden @sm:px-0 @md:px-4 @lg:px-4 @xl:px-6 @2xl:px-8 flex flex-col gap-6 @lg:grid @lg:grid-cols-[2fr_1fr] @xl:grid-cols-[3fr_1fr]" aria-labelledby="results-title">
        <h2 id="results-title" className="sr-only">
          <Msg id="search.results.title" defaultMessage="Job Search Results" description="Screen reader heading for the job search results section" />
        </h2>
        {/* Main hits flow: responsive grid (md:2, lg:3, xl:4 columns) */}
        <ul className="srp-results-grid">
          {/* Small screens (< lg): interleave banners after 5th hit when present, else just hits */}
          <div className="@lg:hidden contents">
            {staticBanners.length > 0
              ? (() => {
                  const elements = [];
                  const interval = Math.ceil((hits.length - 5) / staticBanners.length);
                  let bannerIdx = 0;
                  for (let idx = 0; idx < hits.length; idx++) {
                    elements.push(
                      <li
                        key={(hits[idx] as unknown as DisplayJob).objectID}
                        className="srp-results-grid-item"
                      >
                        <Hit hit={hits[idx] as unknown as DisplayJob} sendEvent={sendEvent} variant={variant} index={idx} />
                      </li>,
                    );
                    if (idx >= 5 && bannerIdx < staticBanners.length && (idx - 5) % interval === 0) {
                      elements.push(
                        <li key={`banner-${bannerIdx}`}>
                          {React.cloneElement(staticBanners[bannerIdx], { key: `banner-${bannerIdx}` })}
                        </li>
                      );
                      bannerIdx++;
                    }
                  }
                  return elements;
                })()
              : hits.map((hit, index) => (
                  <li
                    key={(hit as unknown as DisplayJob).objectID}
                    className="srp-results-grid-item"
                  >
                    <Hit hit={hit as unknown as DisplayJob} sendEvent={sendEvent} variant={variant} index={index} />
                  </li>
                ))}
          </div>
          <div className="hidden @lg:contents">
            {hits.map((hit, index) => (
              <li
                key={(hit as unknown as DisplayJob).objectID}
                className="srp-results-grid-item"
              >
                <Hit hit={hit as unknown as DisplayJob} sendEvent={sendEvent} variant={variant} index={index} />
              </li>
            ))}
          </div>
        </ul>
        {/* Large screens: sticky banners on the right */}
        <div className="hidden @lg:flex flex-col gap-4 sticky top-24 h-fit">{staticBanners.map((banner) => banner)}</div>
      </section>
    );
  };

  return (
    <PerformanceProfiler id="CustomHits">
      {renderContent()}
    </PerformanceProfiler>
  );
}, areCustomHitsPropsEqual);
